import { createContext, useRef, useContext, useCallback } from 'react';

import { generateUUID } from '@/lib/utils';

export interface RefetchCellDataContextValue {
  registerRefetch: (refetch: () => Promise<any>) => () => void;
  refetchAll: () => Promise<PromiseSettledResult<any>[]>;
}

const defaultContextValue = Symbol();

const RefetchCellDataContext = createContext<
  RefetchCellDataContextValue | typeof defaultContextValue
>(defaultContextValue);

export const useRefetchCellDataContext = () => {
  const context = useContext(RefetchCellDataContext);
  if (context === defaultContextValue) {
    throw new Error(
      'useRefetchCellDataContext must be used within a RefetchCellDataContextProvider',
    );
  }
  return context;
};

interface RefetchCellDataContextProviderProps {
  children: React.ReactNode;
}

// Make sure to use this via useRefetchCellData hook for proper clean-up
export const RefetchCellDataContextProvider = ({
  children,
}: RefetchCellDataContextProviderProps) => {
  const refetchCallbacks = useRef<Map<string, () => Promise<any>>>(new Map());

  const registerRefetch = useCallback((refetch: () => Promise<any>) => {
    const id = generateUUID();
    refetchCallbacks.current.set(id, refetch);

    return () => {
      refetchCallbacks.current.delete(id);
    };
  }, []);

  const refetchAll = () => {
    const callbacks = Array.from(refetchCallbacks.current.values());
    return Promise.allSettled(callbacks.map((refetch) => refetch()));
  };

  return (
    <RefetchCellDataContext.Provider value={{ registerRefetch, refetchAll }}>
      {children}
    </RefetchCellDataContext.Provider>
  );
};
